import { useCallback, useRef, useState } from 'react';
import type { Nullable } from 'corgi-types';

import { Key } from 'common/enums/Keyboard';

interface UseRovingTabIndexOptions {
  itemCount: number;
  initialIndex?: number;
}

export const useRovingTabIndex = ({ itemCount, initialIndex = 0 }: UseRovingTabIndexOptions) => {
  const [focusedIndex, setFocusedIndex] = useState(initialIndex);
  const itemRefs = useRef<Array<Nullable<HTMLButtonElement>>>([]);

  const getTabIndex = (index: number): 0 | -1 => (index === focusedIndex ? 0 : -1);

  const getRef = (index: number) => (el: Nullable<HTMLButtonElement>) => {
    itemRefs.current[index] = el;
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const lastIndex = itemCount - 1;
      let nextIndex: Nullable<number> = null;

      switch (event.key) {
        case Key.ArrowRight:
          nextIndex = focusedIndex < lastIndex ? focusedIndex + 1 : 0;
          break;
        case Key.ArrowLeft:
          nextIndex = focusedIndex > 0 ? focusedIndex - 1 : lastIndex;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = lastIndex;
          break;
        default:
          return;
      }

      event.preventDefault();
      setFocusedIndex(nextIndex);
      itemRefs.current[nextIndex]?.focus();
    },
    [focusedIndex, itemCount]
  );

  return { focusedIndex, setFocusedIndex, getTabIndex, getRef, handleKeyDown };
};
